import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent } from '@tark/shared/components';
import { cn } from '@tark/shared/lib';
import { useAuth, useCoachLeadInbox, useTenantBootstrap, useUpdateCoachProviderLead } from '@tark/shared/hooks';
import type { TenantCoachLead } from '@tark/shared/types';
import CoachPageHeader from '../components/CoachPageHeader';
import {
  ArrowRightLeft,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Inbox,
  Loader2,
  MessageSquareText,
  Reply,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';

const MANAGE_ROLES = ['OWNER', 'ADMIN', 'COACH', 'STAFF'];

const FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'CONTACTS', label: 'Contacts' },
  { key: 'TRIALS', label: 'Trials' },
  { key: 'REQUESTS', label: 'Requests' },
  { key: 'WAITLIST', label: 'Waitlist' },
] as const;

const CONTACT_ACTIONS = [
  { status: 'IN_PROGRESS', label: 'Working', icon: Clock3 },
  { status: 'RESPONDED', label: 'Responded', icon: Reply },
  { status: 'COMPLETED', label: 'Done', icon: CheckCircle2 },
  { status: 'CLOSED', label: 'Close', icon: XCircle },
] as const;

type LeadFilter = (typeof FILTERS)[number]['key'];

export default function CoachLeadsPage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const navigate = useNavigate();
  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const canManageLeads = MANAGE_ROLES.includes(activeTenant?.roleCode?.toUpperCase() || '');

  const [filter, setFilter] = useState<LeadFilter>('ALL');
  const leadInbox = useCoachLeadInbox(Boolean(user) && isCoachStudio && canManageLeads);
  const updateLead = useUpdateCoachProviderLead();

  const filteredLeads = useMemo(() => {
    const leads = leadInbox.data?.leads || [];
    if (filter === 'ALL') {
      return leads;
    }
    if (filter === 'CONTACTS') {
      return leads.filter((lead) => lead.leadType === 'MARKETPLACE_CONTACT');
    }
    if (filter === 'TRIALS') {
      return leads.filter((lead) => lead.leadType === 'TRIAL_REQUEST');
    }
    if (filter === 'REQUESTS') {
      return leads.filter((lead) => lead.leadType === 'BOOKING_REQUEST');
    }
    return leads.filter((lead) => lead.leadType === 'WAITLIST');
  }, [filter, leadInbox.data?.leads]);

  const handleStatus = (lead: TenantCoachLead, requestStatus: string) => {
    if (!lead.providerRequestId) {
      return;
    }
    updateLead.mutate({
      requestId: lead.providerRequestId,
      payload: {
        requestStatus,
        providerNote: lead.note || undefined,
        assignToMe: true,
      },
    });
  };

  const handlePrepareAccess = (lead: TenantCoachLead) => {
    navigate(buildLeadAccessRoute(lead));
  };

  if (!isCoachStudio || !canManageLeads) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Inbox className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Coach Studio</p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">Leads need a Coach Studio</h1>
              <p className="mt-2 text-sm text-text-muted">
                Switch into an enabled coach workspace to manage public contacts, trials, and class requests.
              </p>
            </div>
            <Button leftIcon={<ArrowRightLeft className="h-4 w-4" />} onClick={() => navigate('/welcome')}>
              Open Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (leadInbox.isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (leadInbox.isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-text">Leads unavailable</h1>
              <p className="mt-2 text-sm text-text-muted">Refresh and we will restore the inbox for this Coach Studio.</p>
            </div>
            <Button variant="outline" onClick={() => leadInbox.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const summary = leadInbox.data?.summary;
  const tenantName = leadInbox.data?.activeTenant?.displayName || activeTenant?.tenant?.displayName || 'Coach Studio';
  const linkedProvider = leadInbox.data?.linkedProvider;

  return (
    <div className="w-full space-y-5">
      <CoachPageHeader
        title="Leads"
        subtitle="Review public contacts, trials, booking requests, and waitlist interest."
        meta={
          <>
            <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
              {tenantName}
            </span>
            {linkedProvider?.publiclyVisible ? (
              <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                Public Profile Live
              </span>
            ) : null}
          </>
        }
        action={
          <Button
            leftIcon={<CalendarClock className="h-4 w-4" />}
            onClick={() => navigate('/schedule')}
          >
            Open Schedule
          </Button>
        }
      />

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MetricTile label="Open" value={summary?.openCount ?? 0} icon={<Inbox className="h-4 w-4" />} strong />
        <MetricTile label="New" value={summary?.newContactCount ?? 0} icon={<Sparkles className="h-4 w-4" />} />
        <MetricTile label="Trials" value={summary?.trialRequestCount ?? 0} icon={<Users className="h-4 w-4" />} />
        <MetricTile label="Requests" value={summary?.bookingRequestCount ?? 0} icon={<CalendarClock className="h-4 w-4" />} />
        <MetricTile label="Waitlist" value={summary?.waitlistCount ?? 0} icon={<Clock3 className="h-4 w-4" />} />
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-text-muted">
            <Search className="h-4 w-4" />
            <span>{filteredLeads.length} shown</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                  filter === item.key
                    ? 'border-primary bg-primary [color:var(--color-on-primary)]'
                    : 'border-border bg-background text-text-muted hover:border-primary/30 hover:text-text'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {filteredLeads.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-surface px-5 py-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Inbox className="h-6 w-6" />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-text">
              {leadInbox.data?.emptyState?.title || 'No leads in this view'}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-text-muted">
              {leadInbox.data?.emptyState?.description || 'Switch filters or wait for the next public contact or class request.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLeads.map((lead) => (
              <LeadRow
                key={lead.leadId}
                lead={lead}
                disabled={updateLead.isPending}
                onOpenSchedule={() => navigate('/schedule')}
                onPrepareAccess={handlePrepareAccess}
                onStatus={handleStatus}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MetricTile({
  label,
  value,
  icon,
  strong = false,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  strong?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border px-4 py-3',
        strong ? 'border-primary/25 bg-primary/10' : 'border-border bg-surface'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{label}</p>
        <span className={cn('rounded-lg p-2', strong ? 'bg-primary [color:var(--color-on-primary)]' : 'bg-background text-text-muted')}>
          {icon}
        </span>
      </div>
      <p className="mt-1 text-2xl font-semibold text-text">{value}</p>
    </div>
  );
}

function LeadRow({
  lead,
  disabled,
  onOpenSchedule,
  onPrepareAccess,
  onStatus,
}: {
  lead: TenantCoachLead;
  disabled: boolean;
  onOpenSchedule: () => void;
  onPrepareAccess: (lead: TenantCoachLead) => void;
  onStatus: (lead: TenantCoachLead, requestStatus: string) => void;
}) {
  const Icon = lead.contactLead ? MessageSquareText : CalendarClock;
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-4 shadow-[var(--shadow-card)]">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(180px,0.7fr)_minmax(180px,0.7fr)_auto] lg:items-center">
        <div className="flex min-w-0 gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-text">{lead.title}</h2>
              <span className={cn('rounded-full border px-2.5 py-1 text-[11px] font-semibold', statusClass(lead.status))}>
                {lead.statusLabel}
              </span>
            </div>
            <p className="mt-1 truncate text-sm text-text-muted">{lead.subtitle || lead.sourceLabel}</p>
            {lead.note ? <p className="mt-2 line-clamp-2 text-sm text-text-muted">{lead.note}</p> : null}
          </div>
        </div>

        <div className="min-w-0 text-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">{lead.sourceLabel}</p>
          <p className="mt-1 truncate font-medium text-text">{lead.contactLabel || lead.familyName || lead.contactMethod || '-'}</p>
        </div>

        <div className="min-w-0 text-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-text-subtle">
            {lead.scheduleLead ? 'Session' : 'Activity'}
          </p>
          <p className="mt-1 truncate font-medium text-text">
            {lead.scheduleLead ? formatDateTime(lead.sessionStartsAt) : formatDateTime(lead.updatedAt)}
          </p>
          {lead.locationLabel ? <p className="mt-1 truncate text-xs text-text-muted">{lead.locationLabel}</p> : null}
        </div>

        <div className="flex flex-wrap gap-2 lg:justify-end">
          <Button size="sm" variant="outline" onClick={() => onPrepareAccess(lead)} leftIcon={<ShieldCheck className="h-4 w-4" />}>
            Family Access
          </Button>
          {lead.statusEditable
            ? CONTACT_ACTIONS.map((action) => {
                const ActionIcon = action.icon;
                return (
                  <Button
                    key={action.status}
                    size="xs"
                    variant={lead.status === action.status ? 'secondary' : 'outline'}
                    disabled={disabled || lead.status === action.status}
                    leftIcon={<ActionIcon className="h-3.5 w-3.5" />}
                    onClick={() => onStatus(lead, action.status)}
                  >
                    {action.label}
                  </Button>
                );
              })
            : (
              <Button size="sm" variant="outline" onClick={onOpenSchedule} leftIcon={<CalendarClock className="h-4 w-4" />}>
                {lead.primaryActionLabel || 'Open Schedule'}
              </Button>
            )}
        </div>
      </div>
    </div>
  );
}

function statusClass(status?: string | null) {
  const normalized = (status || '').toUpperCase();
  if (normalized === 'NEW' || normalized === 'REQUESTED') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (normalized === 'IN_PROGRESS' || normalized === 'TRIAL_REQUESTED') {
    return 'border-sky-200 bg-sky-50 text-sky-700';
  }
  if (normalized === 'RESPONDED' || normalized === 'WAITLISTED') {
    return 'border-primary/20 bg-primary/10 text-primary';
  }
  if (normalized === 'COMPLETED') {
    return 'border-primary/20 bg-primary/10 text-primary';
  }
  return 'border-border bg-background text-text-muted';
}

function buildLeadAccessRoute(lead: TenantCoachLead): string {
  const params = new URLSearchParams();
  const contact = inferLeadContact(lead);
  params.set('source', 'lead');
  params.set('leadId', lead.leadId);
  params.set('autoCode', '1');
  params.set('label', buildLeadAccessLabel(lead));
  params.set('note', buildLeadInviteNote(lead));
  if (contact.email) {
    params.set('email', contact.email);
  }
  if (contact.phone) {
    params.set('phone', contact.phone);
  }
  return `/tenant-access?${params.toString()}#setup`;
}

function buildLeadAccessLabel(lead: TenantCoachLead): string {
  const person = lead.familyName || lead.personName || lead.contactLabel || lead.title;
  return [person, 'family access'].filter(Boolean).join(' - ');
}

function buildLeadInviteNote(lead: TenantCoachLead): string {
  return [
    `Prepared from ${lead.sourceLabel || 'lead'}: ${lead.title}`,
    lead.subtitle,
    lead.sessionTitle ? `Session: ${lead.sessionTitle}` : null,
    lead.programName ? `Program: ${lead.programName}` : null,
    lead.note,
  ]
    .filter(Boolean)
    .join('\n');
}

function inferLeadContact(lead: TenantCoachLead): { email?: string; phone?: string } {
  const value = (lead.contactLabel || '').trim();
  if (!value) {
    return {};
  }
  const method = (lead.contactMethod || '').toUpperCase();
  if (method.includes('EMAIL') || /\S+@\S+\.\S+/.test(value)) {
    return { email: value };
  }
  return { phone: value };
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}
